import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';
import { BillingInfoTooltip } from './BillingInfoTooltip';
import { TokenRefillTable } from '~/components/dialogs/settings/TokenUsage/TokenRefillTable';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { getOrganizationSubscriptionInfo } from '~/lib/.client/api/organizations';
import { chatStore } from '~/lib/stores/chat';
import { userStore } from '~/lib/stores/user';
import type { OrganizationData } from '~/types/organization';
import { formatSeats, formatTokens } from '~/utils/format';
import { formatPriceInCents } from '~/utils/subscription';

interface TeamTokensInfoProps {
  activeOrganization: OrganizationData;
}

export const TeamTokensInfo = ({ activeOrganization }: TeamTokensInfoProps) => {
  const tokenStats = useStore(chatStore.tokenStats);
  const subscription = activeOrganization?.billing?.subscription;
  const hasActiveSubscription = subscription?.status === 'active';
  const nextMonthStartTimestamp = subscription?.nextMonthStartTimestamp;
  const quantity = subscription?.quantity;
  const interval = subscription?.interval;

  const [invoiceAmount, setInvoiceAmount] = useState<number | null>(null);
  const [isInvoiceInfoLoading, setIsInvoiceLoading] = useState(true);

  const user = useStore(userStore.user);

  useEffect(() => {
    getOrganizationSubscriptionInfo({ organization: activeOrganization })
      .then((subscription) => {
        if (subscription?.nextInvoiceAmount) {
          setInvoiceAmount(subscription.nextInvoiceAmount);
        }
      })
      .finally(() => {
        setIsInvoiceLoading(false);
      });
  }, []);

  if (!user?.featureFlags.teamManagement) {
    return null;
  }

  return hasActiveSubscription ? (
    <>
      <TokenRefillTable
        invoice={{
          title: 'Next Invoice',
          content: isInvoiceInfoLoading ? (
            <LoadingDots text="Loading" className="text-base justify-start h-8" />
          ) : quantity && invoiceAmount ? (
            <span className="flex items-end gap-1">
              {formatPriceInCents(invoiceAmount)}

              <BillingInfoTooltip
                value={`${formatPriceInCents(invoiceAmount / (interval === 'yearly' ? 12 : 1) / quantity)} x ${formatSeats(quantity)} ${interval === 'yearly' ? `x 12 months` : ''}`}
              />
            </span>
          ) : (
            '-'
          ),
          date:
            subscription.interval === 'yearly' ? subscription.currentPeriodEnd : subscription.nextMonthStartTimestamp,
        }}
        refill={{
          title: 'Next Token Refill',
          content:
            tokenStats && quantity ? (
              <span className="flex items-end gap-1">
                {formatTokens(tokenStats.maxPerMonth * quantity)}

                <BillingInfoTooltip value={`${formatTokens(tokenStats.maxPerMonth)} x ${formatSeats(quantity)}`} />
              </span>
            ) : (
              '-'
            ),
          date: nextMonthStartTimestamp,
        }}
      />
    </>
  ) : null;
};
